import React, {useContext, useEffect, useRef, useState} from "react";

import {Elements, CardElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';

import {
    Card,
    Button,
    message,
    Upload,
    Divider,
    Space,
    Tooltip,
    Popconfirm,
    Tabs,
    Badge,
    Row,
    Col,
    Alert,
    Tag, Table, Skeleton, Descriptions, Statistic, Select as AntSelect,
    notification, InputNumber, Progress, Modal
} from 'antd';

const {TabPane} = Tabs;

import {Formik} from 'formik';
import {Form, FormItem, Input, SubmitButton, Radio, Checkbox, Select, Switch, DatePicker} from "formik-antd";
import filesize from 'filesize'

import api from "../api";
import { handleApiError } from "../helpers/formatApiError";
import {
    MinusOutlined,
    DeleteOutlined,
    CreditCardOutlined,
    CheckOutlined,
    ShoppingOutlined,
    EditOutlined,
    CloseCircleOutlined,
    UserSwitchOutlined,
    EyeOutlined,
    DollarOutlined,
    GiftOutlined,
    ClockCircleOutlined,
    LineChartOutlined, ThunderboltOutlined, HddOutlined, MailOutlined, RollbackOutlined, RobotOutlined, SendOutlined, PlusOutlined
} from "@ant-design/icons";

import {
    BrowserView,
    MobileView,
    isBrowser,
    isMobile
} from "device-detect";

import useCurrentOrg from "../helpers/useCurrentOrg";
import {SessionContext} from "../../contexts/SessionContext";
import OrgNavLink from "../helpers/OrgNavLink";
import TimeAgo from "../helpers/TimeAgo";
import {InvoiceIcon} from "../helpers/icons";
import {AbilityContext} from "../helpers/Can";
import HelpPopover from "../HelpPopover";
import moment from "moment";
import setTitle from "../helpers/setTitle";
import useCurrentUser from "../helpers/useCurrentUser";
import ResetButton from "formik-antd/src/reset-button/index";
import {useTranslation} from "react-i18next";
import {confirmBillingContract, createLatestBillingRequestGuard} from "./billingContractConfirmation";

export default ({afterUpdate}) => {
    const {t} = useTranslation();
    setTitle(t('plan-and-billing','Plan and Billing'))

    const org = useCurrentOrg();
    const {dispatch: sessionDispatch} = useContext(SessionContext);

    const stripePromise = loadStripe(Config.stripePublicKey);

    const [attrs, setAttrs] = useState()
    useEffect(()=>{
        if(org) setAttrs(org)
    }, [org?.id])

    const tabHash = location.hash?.replace('#','') || 'plan'
    const [tab, setTab] = useState(tabHash);

    useEffect(()=>{
        setTab(tabHash)
    },[location.hash])

    const tabClick = key =>{
        setTab(key)
        location.hash = key
    }

    const [canceling, setCanceling] = useState()
    const cancelSubscription = ()=>{
        setCanceling(true)
        api.post(`/api/organizations/${org.id}/cancel_subscription`).then(res => {
            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            setCanceling(false)
            message.success(t(`message-subscription-canceled`,`Subscription canceled.`))
        })
    }

    const card = org?.stripe_details?.card

    const statusColors = {
        'active': 'green',
        'past_due': 'red',
        'canceled': 'grey'
    }

    // -----------
    // Super Admin
    // -----------

    const currentUser = useCurrentUser()

    const [togglingInvoicing, setTogglingInvoicing] = useState()

    const toggleInvoicing = ()=>{
        setTogglingInvoicing(true)
        api.post(`/api/organizations/${org.id}/toggle_invoicing`).then(res => {
            setTogglingInvoicing(false)
            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            message.success('Invoicing toggled!')
        })
    }

    const [togglingFree, setTogglingFree] = useState()
    const toggleFree = ()=>{
        setTogglingFree(true)
        api.post(`/api/organizations/${org.id}/toggle_free`).then(res => {
            setTogglingFree(false)
            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            message.success('Free toggled!')
        })
    }

    const [resettingSubscription, setResettingSubscription] = useState()
    const resetSubscription = ()=>{
        setResettingSubscription(true)
        api.post(`/api/organizations/${org.id}/reset_subscription`).then(res => {
            setResettingSubscription(false)

            if(res.data.error) {
                notification.error({
                    message: t('card-error','Card Error'),
                    description: JSON.stringify(res.data.error)
                })
                return
            }

            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            message.success('Subscription Reset!')
        })
    }

    const renderPrice = (code, interval) => {
        if(org?.free) return 'free'
        const yearly = interval == 'year'

        const sp = StripeProducts[code]
        return sp?.amount && `$${n(((sp.amount / 100.0) * (yearly && !sp.yearly_only ? 12 : 1) * (yearly && !sp.yearly_only ? YearDiscount : 1)).toFixed(2))}/${interval == 'year' ? 'yr' : 'mo'}`
    }

    const [activating, setActivating] = useState()
    const activate = ()=>{
        setActivating(true)
        api.post(`/api/organizations/${org.id}/activate_subscription`).then(res => {
            setActivating(false)

            if(res.data.error) {
                notification.error({
                    message: t('card-error','Card Error'),
                    description: JSON.stringify(res.data.error)
                })
                return
            }

            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            message.success(t(`message-subscription-activated`,`Subscription activated.`))
        })
    }

    const cancelable = ['active', 'past_due','unpaid'].indexOf(org?.stripe_status) != -1

    const storedCoupon = org?.stripe_coupon?.coupon
    const coupon = org?.stripe_coupon?.discounts?.[0] || storedCoupon?.table || storedCoupon

    const [settingLevel, setSettingLevel] = useState()

    const adminUpdateFreeAccountLevel = stripe_product_code => {
        setSettingLevel(true)
        api.post(`/api/organizations/${org.id}/set_product_code`, {stripe_product_code}).then(res => {
            setSettingLevel(false)
            sessionDispatch({type: 'org_updated', org: res.data});
            setAttrs(res.data)
            message.success(t('message-account-level-updated','Account Level updated!'))
        })
    }

    if((org?.custom_pricing || org?.private_pricing) && !org?.billing_contract) {
        return (
            <Row>
                <Col xs={24} lg={12}>
                    <Alert
                        type={'info'}
                        showIcon
                        style={{margin:'2em 0'}}
                        message={
                            <>
                                {t('custom-pricing-enabled', 'Custom Pricing Enabled')}
                            </>
                        }
                    />
                </Col>
            </Row>
        )
    }

    return (
        <Tabs
            activeKey={tab}
            onTabClick={tabClick}
        >
            <TabPane tab="Plan" key="plan">
                <Row gutter={16}>
                    <Col lg={12} xs={24}>
                        <Space direction={'vertical'} style={{width:'100%'}}>
                            <Card title={<><ShoppingOutlined /> {t('subscription','Subscription')} <div style={{float:'right'}}><Tag color={statusColors[org?.stripe_status]}>{org?.free ? t('free','Free') : t(org?.stripe_status, org?.stripe_status?.toProperCase()) || t('pending','Pending')}</Tag></div></>}>
                                {(!org?.free || !org?.custom_pricing) && (
                                    <>
                                        {coupon && !org?.billing_contract && (
                                            <Alert
                                                type={'success'} showIcon
                                                style={{marginBottom:'1em'}}
                                                // description={'Discount'}
                                                message={
                                                    <>
                                                        {t('active-discount','Active Discount')}: <strong>{coupon.name} - {coupon.percent_off}% {t('off','off')} {coupon.duration}</strong>
                                                    </>
                                                }
                                            />
                                        )}

                                        {(org?.stripe_status == 'unpaid' || org?.stripe_status == 'past_due') && (
                                            <Alert
                                                type={'error'} showIcon
                                                style={{marginBottom:'1em'}}
                                                message={t('subscription-status','Subscription Status')}
                                                description={
                                                    <>
                                                        {t('your-account-is','Your account is {{status}}', {status: org.stripe_status})}. {t('please','Please')} <OrgNavLink to={'/manage/billing#payment-method'}>{t('update-your-payment-method','update your Payment Method')}</OrgNavLink>.
                                                    </>
                                                }
                                            />
                                        )}

                                        {org?.stripe_status == 'trialing' && (
                                            <Alert
                                                type={'info'} showIcon
                                                style={{marginBottom:'1em'}}
                                                message={t('your-account-is-in-trial-mode','Your Account is in Trial Mode')}
                                                description={
                                                    <>
                                                        {card ? (
                                                            <>{t('activate-now-to-get-full-account-features.','Activate now to get full account features and storage.')}</>
                                                        ) : (
                                                            <>{t('your-free-trial-ends','Your free trial ({{size}} GB) ends', {size: TrialGBLimit})} <TimeAgo date={org.trial_ends_at}/>.</>
                                                        )}
                                                    </>
                                                }
                                                action={
                                                    <>
                                                        <Popconfirm onConfirm={activate} title={t('confirm-are-you-sure','Are you sure?')}>
                                                            <Button icon={<CheckOutlined/>} disabled={!card && !org?.invoicing && !org?.free} loading={activating} type={'primary'}>{t('button-activate','Activate')}</Button>
                                                        </Popconfirm>
                                                    </>
                                                }
                                            />
                                        )}

                                        {!card && !org?.invoicing && !org?.free && (
                                            <Alert
                                                type={'warning'} showIcon
                                                style={{marginBottom:'1em'}}
                                                message={
                                                    <>
                                                        {t('please','Please')} <OrgNavLink to={'/manage/billing#payment-method'}>{t('add-payment-method','Add a Payment Method')}</OrgNavLink> {t('to-upgrade','to upgrade')}.
                                                    </>
                                                }
                                            />
                                        )}
                                    </>
                                )}

                                {org?.billing_contract ? (
                                    <BillingContractSummary key={org.id} org={org}/>
                                ) : (
                                <Formik
                                    initialValues={attrs}
                                    enableReinitialize={true}
                                    onSubmit={(values, actions) => {
                                        const data = _.pick(values, 'stripe_interval','stripe_product_code','storage_tier')
                                        api.post(`/api/organizations/${org.id}/update_plan`, data).then((res)=>{
                                            sessionDispatch({type: 'org_updated', org: res.data});
                                            setAttrs(res.data)

                                            actions.setSubmitting(false)
                                            message.success(t(`plan-updated!`,`Plan Updated!`))

                                            actions.resetForm({});

                                            afterUpdate && afterUpdate()

                                        }).catch((error)=>{
                                            if(error) {
                                                console.error(error.response?.data)
                                                // handleApiError(error)
                                                actions.setSubmitting(false)
                                            }
                                        })
                                    }}
                                >
                                    {({setFieldValue, values, submitForm, dirty, isSubmitting}) => (
                                        <Form layout='vertical'>
                                            <Row gutter={16}>
                                                <Col span={24}>
                                                    <Space direction={'vertical'} size={'middle'}>
                                                        <FormItem name={'stripe_interval'} label={t('payment-plan','Payment Plan')}>
                                                            <Radio.Group name={'stripe_interval'} disabled={!card && !org?.invoicing}>
                                                                <Space direction="vertical">
                                                                    <Radio value={'month'}>{t('monthly','Monthly')}</Radio>
                                                                    <Radio value={'year'}>{t('yearly','Yearly')} <Tag color={'blue'}>{Math.ceil((1 - YearDiscount) * 100)}% {t('off','off')}</Tag></Radio>
                                                                </Space>
                                                            </Radio.Group>
                                                        </FormItem>

                                                        <FormItem name={'stripe_product_code'} label={<>{t('account-level','Account Level')} <small style={{marginLeft:'1em'}}><a href={'https://www.mediagraph.io/pricing'} target={'_blank'} style={{textDecoration:'underline'}}>{t('compare','Compare')}</a></small></>}>
                                                            <Radio.Group name={'stripe_product_code'} onChange={()=> setFieldValue('storage_tier', 0) }>
                                                                <Space direction="vertical">
                                                                    {Object.keys(StripeProducts).map(key => {
                                                                        const sp = StripeProducts[key]
                                                                        const current = key === org?.stripe_product_code

                                                                        if(sp.legacy && !current) return <></>;

                                                                        return (
                                                                            <Radio key={key} value={key} disabled={!card && !org?.invoicing && key != 'free'}>
                                                                                {t(sp.name,sp.name)}  <small>{renderPrice(key, values?.stripe_interval)}</small>

                                                                                {current && (
                                                                                    <Tag color={'green'} style={{marginLeft:'1em'}}>{t('current-plan','Current Plan')}</Tag>
                                                                                )}
                                                                            </Radio>
                                                                        );
                                                                    })}
                                                                </Space>
                                                            </Radio.Group>
                                                        </FormItem>

                                                        <FormItem name={'storage_tier'} label={<>{t('included-storage','Included Storage')} <HelpPopover code={'storage_tier'}/></>}>
                                                            <Radio.Group name={'storage_tier'} disabled={!card && !org?.invoicing}>
                                                                <Space direction={'vertical'} style={{width:'100%'}}>
                                                                    {StripeProducts[values?.stripe_product_code]?.prices?.map((price,i) => (
                                                                        price.to != 'inf' &&
                                                                        <Radio
                                                                            value={i}
                                                                            key={i}
                                                                            // disabled={values.stripe_product_code == org?.stripe_product_code && i < org.storage_tier}
                                                                        >
                                                                            {price.to < 1024 ? `${price.to}GB` : `${price.to / 1000}TB`}
                                                                            &nbsp;<small>
                                                                                {price.fee && (
                                                                                    <>
                                                                                        +${n(((price.fee * (values.stripe_interval == 'year' ? YearDiscount : 1))/ 100.0).toFixed(2))}/mo
                                                                                    </>
                                                                                )}
                                                                            </small>

                                                                            {values.stripe_product_code == org?.stripe_product_code && i == org.storage_tier && (
                                                                                <Tag color={'green'} style={{marginLeft:'1em'}}>{t('current-storage-plan','Current Storage Plan')}</Tag>
                                                                            )}
                                                                        </Radio>
                                                                    ))}
                                                                </Space>
                                                            </Radio.Group>
                                                        </FormItem>

                                                        <Popconfirm onConfirm={submitForm} title={t('confirm-update-plan','Update Plan?')}>
                                                            <Button
                                                                disabled={
                                                                    (!org || (!card && !org?.invoicing)) ||
                                                                    (values?.stripe_product_code == org?.stripe_product_code &&
                                                                        values?.stripe_interval == org?.stripe_interval &&
                                                                        values?.storage_tier == org?.storage_tier)
                                                                }
                                                                icon={<CheckOutlined/>}
                                                                type={'primary'}
                                                                loading={isSubmitting}
                                                            >
                                                                {t('button-update-plan','Update Plan')}
                                                            </Button>
                                                        </Popconfirm>
                                                    </Space>
                                                </Col>
                                            </Row>

                                        </Form>
                                    )}
                                </Formik>
                                )}
                            </Card>
                        </Space>
                    </Col>
                    <Col lg={12} xs={24}>
                        <Space direction={'vertical'} style={{width:'100%'}}>
                            <Card title={<><LineChartOutlined/> {t('current-usage','Current Usage')}</>}>
                                <CurrentUsage/>
                            </Card>

                            {org?.cai_enabled && (
                                <CaiBudgetOverview/>
                            )}

                            {!org?.free && (
                                <Card title={<><EditOutlined/> {t('management','Management')}</>}>
                                    <Popconfirm
                                        title={cancelable && t('confirm-cancel-subscription?','Cancel Subscription?')}
                                        onConfirm={cancelable && cancelSubscription}
                                    >
                                        <Button danger icon={<CloseCircleOutlined/>} loading={canceling} ghost disabled={!cancelable || org?.billing_contract?.collection_method === 'send_invoice'}>{t('button-cancel-subscription','Cancel Subscription')}...</Button>
                                    </Popconfirm>
                                </Card>
                            )}

                            {currentUser?.super_admin && (
                                <Card title={<><ThunderboltOutlined /> Super Admin</>}>
                                    <Space direction={'vertical'}>
                                        <Space>
                                            <Button
                                                icon={<EyeOutlined/>}
                                                href={`https://dashboard.stripe.com/${Config.releaseStage == 'development' ? 'test/' : ''}customers/${org?.stripe_customer_id}`}
                                                target={'_blank'}
                                                type={'text'}
                                            >
                                                View on Stripe
                                            </Button>

                                            <Button
                                                icon={<EditOutlined/>}
                                                href={`https://dashboard.stripe.com/${Config.releaseStage == 'development' ? 'test/' : ''}subscriptions/${org?.stripe_subscription_id}/edit`}
                                                target={'_blatnk'}
                                                type={'text'}
                                            >
                                                Edit on Stripe
                                            </Button>

                                            <Button
                                                icon={<UserSwitchOutlined/>}
                                                href={`/admin/organizations/${org?.id}`}
                                                type={'text'}
                                            >
                                                View in Admin
                                            </Button>
                                        </Space>

                                        {!org?.billing_contract && <Space>
                                            <Popconfirm onConfirm={toggleInvoicing} title={'Toggle Invoicing?'}>
                                                <Button icon={<InvoiceIcon/>} loading={togglingInvoicing} type={'primary'} ghost>{org?.invoicing ? 'Disable' : 'Enable'} Invoicing</Button>
                                            </Popconfirm>

                                            <Popconfirm onConfirm={toggleFree} title={'Toggle Free?'}>
                                                <Button icon={<GiftOutlined/>} loading={togglingFree} type={'primary'} ghost>{org?.free ? 'Disable' : 'Enable'} Free</Button>
                                            </Popconfirm>

                                            {(org?.trial_ends_at || org?.stripe_status === 'canceled') && (
                                                <Popconfirm onConfirm={resetSubscription} title={'Reset Subscription?'}>
                                                    <Button icon={<RollbackOutlined />} loading={resettingSubscription} type={'primary'} ghost>Reset Subscription</Button>
                                                </Popconfirm>
                                            )}

                                            {org?.free && (
                                                <AntSelect onChange={adminUpdateFreeAccountLevel} value={org?.stripe_product_code} loading={settingLevel}>
                                                    <AntSelect.Option value={'essential'}>Essential</AntSelect.Option>
                                                    <AntSelect.Option value={'essential_ai'}>Essential AI</AntSelect.Option>
                                                    <AntSelect.Option value={'enterprise2'}>Enterprise</AntSelect.Option>
                                                    <AntSelect.Option value={'agency'}>Agency</AntSelect.Option>
                                                </AntSelect>
                                            )}
                                        </Space>}

                                        {org?.cai_enabled && (
                                            <CaiAdminControls org={org} />
                                        )}
                                    </Space>
                                </Card>
                            )}
                        </Space>
                    </Col>
                </Row>
            </TabPane>

            <TabPane tab={t('payment-method','Payment Method')} key={'payment-method'}>
                <Row gutter={16}>
                    <Col lg={12} xs={24}>
                        <Card title={<><CreditCardOutlined/> {t('payment-info','Payment Info')}</>} style={{margin:'1em auto', backgroundColor:'white'}}>
                            {org?.invoicing ? (
                                <em>{t('your-account-is-set-to-pay-via-invoice','Your Account is set to pay via Invoice.')}</em>
                            ) : (
                                <Elements stripe={stripePromise}>
                                    <CheckoutForm/>
                                </Elements>
                            )}
                        </Card>
                    </Col>

                    <Col lg={12} xs={24}>
                        {!org?.invoicing && (
                            <Card title={<><InvoiceIcon/> {t('invoicing','Invoicing')}</>} style={{margin:'1em auto', backgroundColor:'white'}}>
                                {t('please-contact-us-at','Please contact us at')} <a href={'mailto:billing@mediagraph.com'}>billing@mediagraph.io</a> {t('if-you-would-like-to-request-invoice-billing',"if you'd like to request Invoice billing")}.
                            </Card>
                        )}
                    </Col>
                </Row>
            </TabPane>

            <TabPane tab={t('invoices','Invoices')} key={'invoices'} id={'invoices'}>
                <Space direction={'vertical'} style={{width:'100%'}}>
                    <Row gutter={16}>
                        <Col lg={12} xs={24}>
                            <UpcomingInvoice/>
                        </Col>
                        <Col lg={12} xs={24}>
                            <BillingEmailForm/>
                        </Col>
                    </Row>
                    <InvoicesTable/>
                </Space>
            </TabPane>

            {/*<TabPane tab={'Updates'} key={'updates'}>Updates</TabPane>*/}
        </Tabs>
    );
}

const BillingContractSummary = ({org}) => {
    const {t} = useTranslation();
    const [contract, setContract] = useState(org.billing_contract);
    const [catalog, setCatalog] = useState(org.billing_catalog);
    const [confirming, setConfirming] = useState(false);
    const requestGuard = useRef(createLatestBillingRequestGuard()).current;
    useEffect(() => {
        requestGuard.invalidate();
        setContract(org.billing_contract);
        setCatalog(org.billing_catalog);
        setConfirming(false);
        return () => requestGuard.invalidate();
    }, [
        org?.id,
        org?.billing_contract?.lock_version,
        org?.billing_contract?.discounts,
        org?.billing_contract?.discounted_contract_amount_cents,
        org?.billing_catalog?.version,
    ]);
    const plan = catalog?.plans?.[contract.product_code];
    const term = catalog?.terms?.[contract.term];
    const tier = plan?.tiers?.find(candidate => candidate.up_to_gb === contract.licensed_storage_gb);
    const totalCents = contract.contract_amount_cents ?? (tier && term
        ? Math.round(tier.monthly_amount_cents * term.months * ((100 - term.discount_percent) / 100))
        : null);
    const discounts = contract.discounts || [];
    const discountedTotalCents = contract.discounted_contract_amount_cents;
    const currency = catalog?.currency || 'USD';
    const formatMoney = (cents, amountCurrency = currency) => new Intl.NumberFormat('en-US', {
        style: 'currency', currency: amountCurrency,
    }).format(cents / 100);
    const formatDiscount = discount => {
        const amount = discount.percent_off != null
            ? `${discount.percent_off}% ${t('off', 'off')}`
            : discount.amount_off_cents != null
                ? `${formatMoney(discount.amount_off_cents, discount.currency || currency)} ${t('off', 'off')}`
                : null;
        const duration = discount.ends_at
            ? t('discount-through-date', 'through {{date}}', {date: moment.unix(discount.ends_at).format('LL')})
            : discount.duration === 'forever'
                ? t('ongoing', 'ongoing')
                : null;
        return [discount.name, amount, duration].filter(Boolean).join(' — ');
    };
    const storageLabel = contract.licensed_storage_gb >= 1000
        ? `${contract.licensed_storage_gb / 1000} TB`
        : `${contract.licensed_storage_gb} GB`;
    const usagePercent = contract.licensed_storage_gb > 0
        ? Math.round(((Number(contract.current_storage_gb) || 0) / contract.licensed_storage_gb) * 100)
        : 0;
    const storageExceeded = (Number(contract.current_storage_gb) || 0) > contract.licensed_storage_gb;
    const operatorManagedTransition = contract.storage_transition_mode !== 'automatic';

    const confirmContract = () => {
        const requestToken = requestGuard.start();
        const requestedOrganizationId = org.id;
        setConfirming(true);
        confirmBillingContract({apiClient: api, organizationId: requestedOrganizationId, contract}).then(result => {
            if (!requestGuard.isCurrent(requestToken)) return;
            setContract(result.contract);
            setCatalog(result.catalog);
            if (result.refreshed) {
                message.warning(t('billing-terms-refreshed', 'Billing terms changed and have been refreshed. Please review them before confirming.'));
            } else {
                message.success(t('billing-plan-confirmed', 'Billing plan confirmed.'));
            }
        }).catch(error => {
            if (requestGuard.isCurrent(requestToken)) handleApiError(error);
        }).finally(() => {
            if (requestGuard.isCurrent(requestToken)) setConfirming(false);
        });
    };

    return (
        <Space direction={'vertical'} size={'middle'} style={{width:'100%'}} data-testid={'billing-contract-summary'}>
            {contract.schedule_backed && (
                <Alert type={'warning'} showIcon message={t('scheduled-billing-plan', 'Scheduled billing plan')}
                       description={t('scheduled-billing-plan-detail', 'Renewal changes are reviewed by our billing team because Stripe prebilling is unavailable for schedule-backed subscriptions.')}/>
            )}
            {usagePercent >= 90 && (
                <Alert
                    type={storageExceeded ? 'error' : 'warning'}
                    showIcon
                    message={storageExceeded
                        ? t('licensed-storage-exceeded', 'Licensed storage exceeded')
                        : t('licensed-storage-near-limit', 'Licensed storage is near its limit')}
                    description={storageExceeded
                        ? operatorManagedTransition
                            ? t('licensed-storage-exceeded-review-detail', 'A 15-day decision window is active. Reduce usage below the licensed limit to remain on this tier; otherwise the billing team reviews a transition to the next fitting tier and the prorated difference from the first crossing.')
                            : t('licensed-storage-exceeded-detail', 'A 15-day decision window is active. Reduce usage below the licensed limit to remain on this tier; otherwise the account moves to the next fitting tier and the prorated difference includes the decision window.')
                        : t('licensed-storage-near-limit-detail', 'You have used {{percent}}% of licensed storage.', {percent: usagePercent})}
                />
            )}
            <Progress percent={Math.min(usagePercent, 100)} status={storageExceeded ? 'exception' : 'normal'} />
            <Descriptions bordered size={'small'} column={1}>
                <Descriptions.Item label={t('plan', 'Plan')}>{plan?.name || contract.product_code?.replaceAll('_', ' ')}</Descriptions.Item>
                <Descriptions.Item label={t('term', 'Term')}>{contract.term?.toProperCase()}</Descriptions.Item>
                <Descriptions.Item label={t('licensed-storage', 'Licensed storage')}>{storageLabel}</Descriptions.Item>
                <Descriptions.Item label={t('current-usage', 'Current usage')}>{contract.current_storage_gb?.toLocaleString()} GB</Descriptions.Item>
                {totalCents !== null && (
                    <Descriptions.Item label={discounts.length ? t('list-price', 'List price') : t('contract-total', 'Contract total')}>
                        {formatMoney(totalCents)} / {contract.term}
                    </Descriptions.Item>
                )}
                {discounts.map((discount, index) => (
                    <Descriptions.Item key={discount.id || discount.coupon_id || index} label={t('discount', 'Discount')}>
                        {formatDiscount(discount)}
                    </Descriptions.Item>
                ))}
                {discountedTotalCents != null && (
                    <Descriptions.Item label={t('your-price', 'Your price')}>
                        {formatMoney(discountedTotalCents)} / {contract.term} {t('before-tax', 'before tax')}
                    </Descriptions.Item>
                )}
                <Descriptions.Item label={t('collection-method', 'Collection method')}>{contract.collection_method === 'send_invoice' ? t('invoice', 'Invoice') : t('card', 'Card')}</Descriptions.Item>
                <Descriptions.Item label={t('renewal', 'Renewal')}>{contract.renewal_at ? moment(contract.renewal_at).format('LL') : '—'}</Descriptions.Item>
            </Descriptions>
            {contract.consent_required && !contract.consented_at && contract.collection_method === 'charge_automatically' && (
                <Alert type={'info'} showIcon message={t('confirm-billing-plan', 'Confirm this billing plan')}
                       description={t('confirm-billing-plan-detail', 'Confirm the plan, term, and licensed storage shown above. Changes after confirmation are reviewed before Stripe execution.')}
                       action={<Popconfirm title={t('confirm-billing-plan-question', 'Confirm these billing terms?')} onConfirm={confirmContract}>
                           <Button type={'primary'} loading={confirming}>{t('confirm-plan', 'Confirm plan')}</Button>
                       </Popconfirm>}/>
            )}
            {contract.collection_method === 'send_invoice' && !contract.contract_signed_at && (
                <Alert type={'info'} showIcon message={t('agreement-pending', 'Agreement pending')}
                       description={t('agreement-pending-detail', 'Your signed agreement will be recorded by the billing team. Access remains active while your subscription invoice is open.')}/>
            )}
            <div>
                <a href={'https://www.mediagraph.io/pricing'} target={'_blank'} rel={'noreferrer'}>{t('compare-plans', 'Compare published plans')}</a>
                <span style={{margin:'0 .5em'}}>·</span>
                <a href={'mailto:billing@mediagraph.io'}>{t('request-plan-change', 'Request a plan change')}</a>
            </div>
        </Space>
    );
};

const CaiBudgetOverview = () => {
    const {t} = useTranslation();
    const org = useCurrentOrg();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [budgetAmount, setBudgetAmount] = useState(10);
    const [adding, setAdding] = useState(false);

    const fetchData = () => {
        if (!org?.id) return;
        api(`/api/organizations/${org.id}/cai_usage`).then(res => {
            setData(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, [org?.id]);

    useEffect(() => {
        const handler = () => fetchData();
        window.addEventListener('caiBudgetUpdated', handler);
        return () => window.removeEventListener('caiBudgetUpdated', handler);
    }, [org?.id]);

    // Detect return from Stripe Checkout
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topupStatus = params.get('cai_topup');
        if (topupStatus === 'success') {
            message.success(t('budget-payment-success', 'Payment successful! Your budget will be updated shortly.'));
            const url = new URL(window.location);
            url.searchParams.delete('cai_topup');
            window.history.replaceState({}, '', url.toString());
        } else if (topupStatus === 'canceled') {
            message.info(t('budget-payment-canceled', 'Payment was canceled.'));
            const url = new URL(window.location);
            url.searchParams.delete('cai_topup');
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    const handleAddBudget = async () => {
        if (!budgetAmount || budgetAmount <= 0) return;
        setAdding(true);
        try {
            const res = await api.post(`/api/organizations/${org.id}/add_cai_budget`, {amount: budgetAmount, return_to: 'billing'});
            if (res.data.success && res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
            } else {
                message.error(res.data.error || t('error-adding-budget', 'Error adding budget'));
                setAdding(false);
            }
        } catch (err) {
            message.error(err.response?.data?.error || t('error-adding-budget', 'Error adding budget'));
            setAdding(false);
        }
    };

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4
    }).format(value || 0);

    const getProgressStatus = () => {
        if (!data) return 'normal';
        if (data.budget_exceeded) return 'exception';
        if (data.at_warning_threshold) return 'warning';
        return 'normal';
    };

    const getProgressColor = () => {
        const status = getProgressStatus();
        if (status === 'exception') return '#ff4d4f';
        if (status === 'warning') return '#faad14';
        return '#1890ff';
    };

    return (
        <Skeleton active loading={loading}>
            <Card title={<><RobotOutlined /> {t('ai-budget','AI Budget')}</>}>
                <Progress
                    percent={data?.usage_percentage || 0}
                    status={getProgressStatus()}
                    strokeColor={getProgressColor()}
                    format={(percent) => `${(parseFloat(percent) || 0).toFixed(1)}%`}
                    size="default"
                />
                <div style={{marginTop: 4, marginBottom: 16, color: 'rgba(0,0,0,0.45)', fontSize: 13}}>
                    {formatCurrency(data?.spent_amount)} / {formatCurrency(data?.total_budget)}
                </div>
                <Row gutter={16} align="middle">
                    <Col span={6}>
                        <Statistic title={t('total-budget', 'Total Budget')} value={data?.total_budget || 0} prefix="$" precision={2} />
                    </Col>
                    <Col span={6}>
                        <Statistic title={t('spent', 'Spent')} value={data?.spent_amount || 0} prefix="$" precision={4} />
                    </Col>
                    <Col span={6}>
                        <Statistic title={t('remaining', 'Remaining')} value={data?.remaining_budget || 0} prefix="$" precision={2}
                            valueStyle={{color: data?.budget_exceeded ? '#ff4d4f' : data?.at_warning_threshold ? '#faad14' : '#52c41a'}}
                        />
                    </Col>
                    <Col span={6}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
                            {t('add-budget', 'Add Budget')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Modal
                title={t('add-ai-budget', 'Add AI Budget')}
                open={modalOpen}
                onOk={handleAddBudget}
                onCancel={() => setModalOpen(false)}
                confirmLoading={adding}
                okText={t('add-budget', 'Add Budget')}
            >
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <span>{t('add-ai-budget-description', 'Enter the amount to add to your AI budget. You will be redirected to a secure payment page.')}</span>
                    <div>
                        <strong>{t('amount', 'Amount')}:</strong>
                        <InputNumber style={{width: '100%', marginTop: '8px'}} prefix={<DollarOutlined />}
                            min={1} max={10000} value={budgetAmount} onChange={setBudgetAmount} precision={2}
                        />
                    </div>
                    <span style={{color: 'rgba(0,0,0,0.45)'}}>
                        {t('budget-charge-note-billing', 'You will be redirected to Stripe Checkout to complete the payment.')}
                    </span>
                </Space>
            </Modal>
        </Skeleton>
    );
};

const CaiAdminControls = ({org}) => {
    const {t} = useTranslation();
    const [caiAmount, setCaiAmount] = useState(10);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [granting, setGranting] = useState(false);

    const sendCaiInvoice = () => {
        setSendingInvoice(true);
        api.post(`/api/organizations/${org.id}/admin_add_cai_budget`, {amount: caiAmount}).then(res => {
            setSendingInvoice(false);
            if (res.data.success) {
                message.success('CAI invoice sent!');
            } else {
                message.error(res.data.error || 'Error sending invoice');
            }
        }).catch(err => {
            setSendingInvoice(false);
            message.error(err.response?.data?.error || 'Error sending invoice');
        });
    };

    const grantCaiBudget = () => {
        setGranting(true);
        api.post(`/api/organizations/${org.id}/admin_grant_cai_budget`, {amount: caiAmount}).then(res => {
            setGranting(false);
            if (res.data.success) {
                message.success(`CAI budget granted! New total: $${res.data.new_total_budget}`);
            } else {
                message.error(res.data.error || 'Error granting budget');
            }
        }).catch(err => {
            setGranting(false);
            message.error(err.response?.data?.error || 'Error granting budget');
        });
    };

    return (
        <>
            <Divider />
            <Space>
                <RobotOutlined /> <strong>CAI Budget</strong>
            </Space>
            <div style={{marginTop: 8}}>
                <Space>
                    <InputNumber
                        min={1}
                        max={10000}
                        value={caiAmount}
                        onChange={setCaiAmount}
                        prefix="$"
                        precision={2}
                        style={{width: 120}}
                    />
                    <Popconfirm onConfirm={sendCaiInvoice} title={`Send $${caiAmount} CAI invoice?`}>
                        <Button icon={<SendOutlined />} loading={sendingInvoice} type={'primary'} ghost>Send CAI Invoice</Button>
                    </Popconfirm>
                    <Popconfirm onConfirm={grantCaiBudget} title={`Grant $${caiAmount} CAI budget (free)?`}>
                        <Button icon={<GiftOutlined />} loading={granting} type={'primary'} ghost>Grant Budget</Button>
                    </Popconfirm>
                </Space>
            </div>
        </>
    );
};

const CheckoutForm = ({submitForm})=> {
    const {t} = useTranslation();
    const org = useCurrentOrg()
    const {dispatch: sessionDispatch} = useContext(SessionContext);

    const stripe = useStripe();
    const elements = useElements();

    const [cardComplete, setCardComplete] = useState()
    const [loadingStripeToken, setLoadingStripeToken] = useState()

    const handleSubmit = async (event) => {
        setLoadingStripeToken(true)
        // Block native form submission.
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return;
        }

        // Get a reference to a mounted CardElement. Elements knows how
        // to find your CardElement because there can only ever be one of
        // each type of element.
        const cardElement = elements.getElement(CardElement);

        // Use your card Element with other Stripe.js APIs
        const {error, paymentMethod} = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        });

        if (error) {
            console.log('[error]', error);

            setLoadingStripeToken(false)
        } else {
            console.log('[PaymentMethod]', paymentMethod);

            updatePaymentMethod(paymentMethod.id)
        }
    };

    window.updatePaymentMethod = token => {
        api.post(`/api/organizations/${org.id}/add_payment_method`, {token}).then(res => {
            sessionDispatch({type: 'org_updated', org: res.data});
            setLoadingStripeToken(false)
            message.success(t('message-card-updated','Card updated!'))
            setShowCardForm(false)
        })
    }

    const onCardChange = e =>{
        if(e.complete) setCardComplete(true)
    }

    const card = org?.stripe_details?.card

    const [showCardForm, setShowCardForm] = useState(!card)

    const [removingCard, setRemovingCard] = useState()
    const removeCard = ()=>{
        setRemovingCard(true)
        api.post(`/api/organizations/${org.id}/remove_payment_method`).then(res => {
            setRemovingCard(false)
            sessionDispatch({type: 'set_org', org: res.data});
            message.success(t('message-card-removed','Card removed.'))
        })
    }

    return (
        <form onSubmit={handleSubmit}>
            <Space direction={'vertical'} style={{width:'100%'}}>
                {card && !showCardForm && (
                    <Alert
                        icon={<CreditCardOutlined/>}
                        type={'info'} showIcon
                        message={
                            <>
                                <strong>{card.brand?.toProperCase()}</strong> {t('ending','ending')} **** **** **** {card.last4}
                            </>
                        }
                        action={
                            <Popconfirm
                                title={t('confirm-remove-card','Remove card?')}
                                onConfirm={removeCard}
                            >
                                <Button
                                    icon={<DeleteOutlined/>}
                                    danger
                                    ghost
                                    loading={removingCard}
                                />
                            </Popconfirm>
                        }
                   />
                )}

                {showCardForm && (
                    <>
                        <div style={{border:'1px solid #eee', borderRadius:10, padding:'1em'}}>
                            <CardElement onChange={onCardChange}/>
                        </div>

                        <Button
                            style={{marginTop:'1em'}}
                            disabled={!stripe || !cardComplete}
                            block
                            type={'primary'}
                            icon={<CheckOutlined/>}
                            onClick={handleSubmit}
                            loading={loadingStripeToken}
                        >
                            {t('button-save-card','Save Card')}
                        </Button>

                        {card && (
                            <Button
                                block
                                icon={<CloseCircleOutlined/>}
                                onClick={()=> setShowCardForm(false) }
                            >
                                {t('button-cancel','Cancel')}
                            </Button>
                        )}
                    </>
                )}

                {!showCardForm && (
                    <>
                        <Button
                            block
                            icon={<EditOutlined/>}
                            onClick={()=> setShowCardForm(true) }
                        >
                            {card ? t('change','Change') : t('add','Add')} {t('payment-method','Payment Method')}
                        </Button>
                    </>
                )}
            </Space>
        </form>
    );
}

const InvoicesTable = ({})=>{
    const {t} = useTranslation();
    const currentUser = useCurrentUser();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const [loading, setLoading] = useState(false);
    const [markingPaid, setMarkingPaid] = useState(null);

    const handleTableChange = (pagination, filters, sorter) => {
        setLoading(true);

        const params = {
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination,
            ...filters,
        }
        api.get('/api/invoices', { params: params }).then(res => {
            setData(res.data)
            setPagination({
                ...params.pagination,
                total: res.headers['total-entries']
            })
            setLoading(false)
        })
    }

    const org = useCurrentOrg()

    useEffect(handleTableChange, [org?.updated_at]);

    const markInvoicePaid = (invoice) => {
        setMarkingPaid(invoice.id);
        api.post(`/api/organizations/${org.id}/mark_cai_invoice_paid`, {stripe_invoice_id: invoice.stripe_id}).then(res => {
            setMarkingPaid(null);
            if (res.data.success) {
                message.success(t('invoice-marked-paid', 'Invoice marked as paid. Budget will be updated shortly.'));
                handleTableChange();
            } else {
                message.error(res.data.error || t('error-marking-paid', 'Error marking invoice as paid'));
            }
        }).catch(err => {
            setMarkingPaid(null);
            message.error(err.response?.data?.error || t('error-marking-paid', 'Error marking invoice as paid'));
        });
    };

    const statusColors = {
        'paid': 'green',
        'draft': 'yellow',
        'open': 'red'
    }

    const columns = [
        {
            title: t('date','Date'),
            dataIndex: 'created_at',
            sorter: true,
            width: 200,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t('type','Type'),
            dataIndex: 'invoice_type',
            width: 120,
            render: type => type === 'cai_topup'
                ? <Tag color="purple">{t('ai-topup', 'AI Top-up')}</Tag>
                : <Tag>{t('subscription', 'Subscription')}</Tag>
        },
        {
            title: t('status','Status'),
            dataIndex: 'status',
            render: status => <Tag color={statusColors[status]}>{t(status, status?.toProperCase())}</Tag>
        },
        {
            title: t('total','Total'),
            dataIndex: 'total',
            sorter: true,
            render: total => (
                <>${(total / 100).toFixed(2)}</>
            )
        },
        {
            title: t('details','Details'),
            width: 250,
            render: invoice => {
                return <Space>
                    {invoice.stripe_url &&
                        <Button
                            icon={invoice.status == 'open' ? <DollarOutlined /> : <InvoiceIcon/>}
                            href={invoice.stripe_url}
                            target={'_blank'}
                        >
                            {invoice.status == 'open' ? t('button-pay-invoice','Pay Invoice') : t('button-view-download','View/Download')}
                        </Button>
                    }
                    {currentUser?.super_admin && invoice.status === 'open' && invoice.invoice_type === 'cai_topup' && invoice.stripe_id &&
                        <Popconfirm
                            title={t('mark-paid-confirm', 'Mark this invoice as paid (out of band)?')}
                            onConfirm={() => markInvoicePaid(invoice)}
                        >
                            <Button
                                icon={<CheckOutlined />}
                                loading={markingPaid === invoice.id}
                                type="primary"
                                ghost
                            >
                                {t('mark-paid', 'Mark Paid')}
                            </Button>
                        </Popconfirm>
                    }
                </Space>
            }
        }
    ];

    return (
        <Table
            size={'small'}
            scroll={{x:true}}
            columns={columns}
            rowKey={record => record.id}
            dataSource={data}
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
        />
    )
}


const UpcomingInvoice = ({})=>{
    const {t} = useTranslation();
    const org = useCurrentOrg()

    const [invoice, setInvoice] = useState()
    const [loading, setLoading] = useState(true)
    useEffect(()=>{
        if(!org) return;

        api(`/api/organizations/${org?.id}/upcoming_invoice`).then(res => {
            setInvoice(res.data)
            setLoading(false)
        })
    }, [org?.updated_at])
    const invoiceTimestamp = invoice?.next_payment_attempt || invoice?.due_date || invoice?.period_end ||
        invoice?.lines?.data?.[0]?.period?.end || invoice?.created;
    const invoiceMoment = invoiceTimestamp ? moment.unix(Number(invoiceTimestamp)) : null;

    return (
        <Skeleton active loading={loading}>
            <Card title={<><ClockCircleOutlined/> {t('upcoming-invoice','Upcoming Invoice')}</>} size={'small'}>
                {invoice && (
                    <>
                        <Descriptions bordered size='small' column={1}>
                            <Descriptions.Item label={t('date','Date')}>
                                {invoiceMoment?.isValid() ? <TimeAgo date={invoiceMoment}/> : '—'}
                            </Descriptions.Item>

                            <Descriptions.Item label={t('amount','Amount')}>
                                ${(invoice.amount_due / 100.0).toFixed(2)}
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Card>
        </Skeleton>
    )
}

const CurrentUsage = ({})=>{
    const {t} = useTranslation();
    const org = useCurrentOrg()

    const [usage, setUsage] = useState()
    const [loading, setLoading] = useState(true)
    useEffect(()=>{
        if(!org) return;

        api(`/api/organizations/${org?.id}/current_usage`).then(res => {
            setUsage(res.data)
            setLoading(false)
        })
    }, [org?.updated_at])

    return (
        <Skeleton active loading={loading}>
            <Statistic title={t("storage","Storage")} value={usage || 0} suffix={'GB'} />
        </Skeleton>
    )
}

const BillingEmailForm = ()=>{
    const {t} = useTranslation();
    const org = useCurrentOrg()
    const {dispatch: sessionDispatch} = useContext(SessionContext);

    return (
        <Card title={<><MailOutlined/> Notifications</>} size={'small'}>
            <Formik
                initialValues={org}
                enableReinitialize={true}
                onSubmit={(values, actions) => {
                    const data = _.pick(values, 'billing_email')
                    api.patch(`/api/organizations/${org.id}`, {organization: data}).then((res)=>{
                        sessionDispatch({type: 'org_updated', org: res.data});

                        actions.setSubmitting(false)
                        message.success(t(`message-updated`,`Updated!`))

                        actions.resetForm({});

                    }).catch((error)=>{
                        if(error) {
                            console.error(error.response?.data)
                            handleApiError(error)
                            actions.setSubmitting(false)
                        }
                    })
                }}
            >
                {({setFieldValue, values, submitForm, dirty, isSubmitting}) => (
                    <Form layout='horizontal'>
                        <Space size={'middle'}>
                            <FormItem name={'billing_email'} label={<>{t('billing-email','Billing Email')} <HelpPopover code={'billing-email'}/></>}>
                                <Input name={'billing_email'}/>
                            </FormItem>

                            <FormItem name={'submit'}>
                                <Button
                                    onClick={submitForm}
                                    icon={<CheckOutlined/>}
                                    type={'primary'}
                                    loading={isSubmitting}
                                    disabled={!dirty}
                                >
                                    {t('button-save','Save')}
                                </Button>
                            </FormItem>
                        </Space>
                    </Form>
                )}
            </Formik>
        </Card>

    )
}
